const BASE_URL = 'https://staging.qurve.ai/api';

// ----- Login & Token Storage -----
// On successful login, store tokens under adminaccesstoken and adminrefreshtoken.
export const login = async (username, password) => {
  try {
    const response = await fetch(`${BASE_URL}/testing/login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        username,
        password,
      }),
    });

    if (!response.ok) {
      console.error('Login failed:', await response.text());
      return false;
    }

    const data = await response.json();
    localStorage.setItem("adminaccesstoken", data.access);
    localStorage.setItem("adminrefreshtoken", data.refresh);
    return true;
  } catch (error) {
    console.error('Login error:', error);
    return false;
  }
};

// ----- Token Refresh -----
// Use adminrefreshtoken from localStorage to refresh the access token.
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem("adminrefreshtoken");
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${BASE_URL}/testing/token_refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh: refreshToken,
      }),
    });

    if (!response.ok) {
      console.error('Token refresh failed:', await response.text());
      return false;
    }

    const data = await response.json();
    localStorage.setItem("adminaccesstoken", data.access);
    return true;
  } catch (error) {
    console.error('Token refresh error:', error);
    return false;
  }
};

// ----- Helper for Authenticated Requests -----
const authenticatedRequest = async (url, method = 'POST', body) => {
  const accessToken = localStorage.getItem("adminaccesstoken");
  if (!accessToken) {
    console.error('No access token available');
    return null;
  }

  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) return null;
      return authenticatedRequest(url, method, body);
    }

    if (!response.ok) {
      console.error(`Request failed: ${response.status}`, await response.text());
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Request error:', error);
    return null;
  }
};

// ----- API Functions -----
export const fetchClients = async () => {
  const response = await authenticatedRequest('/testing/fetch-clients/');
  if (!response) return [];
  return response.clients.map((name, index) => ({
    id: index.toString(),
    name,
  }));
};

export const fetchLLMs = async () => {
  const response = await authenticatedRequest('/testing/fetch_llms/');
  if (!response) return [];
  return response.llms.map((name, index) => ({
    id: index.toString(),
    name,
    provider: determineProvider(name),
  }));
};

const determineProvider = (modelName) => {
  if (modelName.includes('gpt')) return 'OpenAI';
  if (modelName.includes('gemini')) return 'Google';
  if (modelName.includes('claude')) return 'Anthropic';
  if (modelName.includes('llama')) return 'Meta';
  return 'Unknown';
};

export const fetchDefaultPrompt = async (clientName) => {
  const response = await authenticatedRequest(
    '/testing/fetch_default_prompt/',
    'POST',
    { client_name: clientName }
  );
  return (response && response.default_prompt) ||
    "You are a helpful assistant that can answer questions and help with tasks.";
};

export const sendChatMessage = async (
  selectedClient,
  selectedLLM,
  promptTemplate,
  message,
  sessionId,
  stream = false
) => {
  if (!selectedClient || !selectedLLM) {
    console.error('Client or LLM not selected');
    return null;
  }
  try {
    if (stream) {
      return null;
    } else {
      const response = await authenticatedRequest(
        '/testing/ragchat_b/',
        'POST',
        {
          selcted_client: selectedClient.name,
          selected_llm: selectedLLM.name,
          prompt_text: promptTemplate,
          session_id: sessionId,
          text: message,
          stream: false
        }
      );

      if (!response) return null;

      return {
        message: response.response || "No response received",
        context: response.contexts || "No context provided",
        confidence: parseFloat(response.scores && response.scores[0] ? response.scores[0] : "0.5")
      };
    }
  } catch (error) {
    console.error('Send chat message error:', error);
    return null;
  }
};

const parseContextString = (contextStr) => {
  try {
    if (typeof contextStr === 'object') return JSON.stringify(contextStr, null, 2);
    if (contextStr === '[]' || !contextStr) return "No context available";
    return contextStr
      .replace(/Document\(id='[^']+',\s*metadata=(\{[^}]+\})/g, 'Source $1')
      .replace(/page_content='([^']+)'/g, '\nContent: $1');
  } catch (error) {
    console.error('Error parsing context:', error);
    return contextStr || "No context available";
  }
};

export const streamChatResponse = async (
  selectedClient,
  selectedLLM,
  promptTemplate,
  message,
  sessionId,
  onChunk
) => {
  if (!selectedClient || !selectedLLM) {
    console.error('Client, LLM not selected or not authenticated');
    return;
  }
  const accessToken = localStorage.getItem("adminaccesstoken");
  if (!accessToken) {
    console.error('No access token available');
    return;
  }
  try {
    console.log('Starting streamChatResponse with:', {
      client: selectedClient.name,
      llm: selectedLLM.name,
      sessionId,
      stream: true
    });

    const response = await fetch(`${BASE_URL}/testing/ragchat_b/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        selcted_client: selectedClient.name,
        selected_llm: selectedLLM.name,
        prompt_text: promptTemplate,
        session_id: sessionId,
        text: message,
        stream: true
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Stream request failed:', response.status, errorText);
      throw new Error(`Stream request failed: ${response.status} ${errorText}`);
    }

    if (!response.body) {
      console.error('No response body');
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let context = '';
    let scores = [];
    let isFirstChunk = true;
    let fullContent = '';

    console.log('Starting to read response stream');

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // Process any remaining buffered data if it forms a complete line.
        if (buffer.trim()) {
          processLine(buffer);
        }
        console.log('Stream reading complete');
        break;
      }
      
      buffer += decoder.decode(value, { stream: true });
      // Split by newline; the last element may be an incomplete line.
      const lines = buffer.split('\n');
      // Retain the last partial line in the buffer.
      buffer = lines.pop();
      
      for (const line of lines) {
        if (line.trim() !== '') {
          processLine(line);
        }
      }
    }

    function processLine(line) {
      if (line.startsWith('data: ')) {
        try {
          const jsonData = line.substring(6);
          console.log('Parsing JSON data:', jsonData);
          const data = JSON.parse(jsonData);
          console.log('Received chunk data:', data);
          
          if (isFirstChunk) {
            console.log('Processing first chunk with data:', data);
            isFirstChunk = false;
            if (data.contexts) {
              context = data.contexts;
              console.log('Captured context from first chunk:', context);
            }
            if (data.scores) {
              try {
                if (typeof data.scores === 'string') {
                  scores = JSON.parse(data.scores);
                  console.log('Parsed scores from string:', scores);
                } else if (Array.isArray(data.scores)) {
                  scores = data.scores;
                  console.log('Using scores array directly:', scores);
                }
                scores = scores.map(score => typeof score === 'string' ? parseFloat(score) : score);
                console.log('Processed scores:', scores);
              } catch (e) {
                console.error('Failed to parse scores:', e);
              }
            }
          }
          
          if (data.chunk) {
            fullContent += data.chunk;
            console.log('Updated full content:', fullContent);
          }
          
          const confidence = scores.length > 0 ? scores[0] : undefined;
          console.log('Calling onChunk with:', {
            chunk: data.chunk || '',
            done: !!data.done,
            fullContent,
            context,
            confidence
          });
          
          onChunk(
            data.chunk || '', 
            !!data.done, 
            fullContent,  
            context,     
            confidence 
          );
          
          if (data.done) {
            console.log('Stream data marked as done');
            reader.cancel();
          }
        } catch (e) {
          console.error('Failed to parse chunk:', e, 'Line:', line);
        }
      }
    }
  } catch (error) {
    console.error('Stream chat response error:', error);
  }
};
